import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const SESSION_ID_KEY = "gfs_session_id";

/** Returns or creates a persistent anonymous session ID stored in localStorage. */
export function getSessionId(): string {
  let id = localStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

/** Fire-and-forget page view tracker. Call once per route change. */
async function recordPageView(path: string, userId: string | null) {
  try {
    await supabase.from("page_views" as any).insert({
      path,
      user_id: userId ?? null,
      session_id: getSessionId(),
    });
  } catch {
    // silent — never break the UI for analytics
  }
}

/** Hook: automatically records a page view on every route change. */
export function usePageTracking() {
  const location = useLocation();
  const { user } = useAuth();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname;
    if (path === lastPath.current) return; // deduplicate same-path renders
    lastPath.current = path;
    recordPageView(path, user?.id ?? null);
  }, [location.pathname, user?.id]);
}

/** Persist a cart snapshot to cart_sessions.
 *  - Only writes when items.length > 0, OR when converted=true (checkout completion).
 *  - Never overwrites an existing record with an empty snapshot to preserve abandoned-cart data.
 */
export async function persistCartSession(
  shop: "premade" | "coloring" | "custom",
  items: { id: string; name: string; price_cents?: number; quantity?: number }[],
  userId: string | null,
  converted = false
) {
  // Skip empty writes entirely — they pollute the analytics table and wipe abandoned snapshots.
  if (items.length === 0 && !converted) return;

  const sessionId = getSessionId();
  try {
    await supabase.from("cart_sessions" as any).upsert(
      {
        session_id: sessionId,
        shop,
        user_id: userId ?? null,
        items,
        item_count: items.reduce((s: number, i: any) => s + (i.quantity ?? 1), 0),
        converted,
        last_updated: new Date().toISOString(),
      },
      { onConflict: "session_id,shop" }
    );
  } catch {
    // silent
  }
}

/** Fire-and-forget product view tracker.
 *  De-duplicates within a session: only records once per product per session.
 */
const viewedThisSession = new Set<string>();

export async function recordProductView(
  productId: string,
  shop: "coloring" | "premade",
  userId: string | null
) {
  const key = `${shop}:${productId}`;
  if (viewedThisSession.has(key)) return; // deduplicate within session
  viewedThisSession.add(key);
  try {
    await supabase.from("product_views" as any).insert({
      product_id: productId,
      shop,
      session_id: getSessionId(),
      user_id: userId ?? null,
    });
  } catch {
    // silent — never break the UI for analytics
  }
}
